import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatLocalDateTime, formatLocalDate } from "../lib/constants";
import { StatusPill } from "../components/StatusPill";
import { PriorityBadge, EtaBadge } from "../components/Badges";
import { downloadCsv } from "../lib/downloadCsv";
import {
  Inbox,
  ChevronRight,
  PackagePlus,
  CalendarClock,
  Wrench,
  Download,
} from "lucide-react";

export default function MyRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shipments", {
          params: { assigned_crew_leader: user?.name || user?.email || "" },
        });
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadCsv({
        url: "/shipments/export",
        params: { assigned_crew_leader: user?.name || user?.email || "" },
        fallbackFilename: `Material_Shipments_${new Date().toISOString().slice(0, 10)}.csv`,
      });
    } catch (e) {
      // downloadCsv toasts; log for dev tools/monitoring.
      console.error("My Requests CSV export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const open = items.filter(
    (s) => !["Completed", "Not Needed"].includes(s.Preparation_Queue_Status)
  );
  const completed = items.filter(
    (s) => s.Preparation_Queue_Status === "Completed"
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-purple-700">
            Crew workspace
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight flex items-center gap-2">
            <Inbox strokeWidth={2.5} className="h-7 w-7" />
            My Requests
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Shipments and material requests assigned to {user?.name || user?.email}.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting || items.length === 0}
            data-testid="my-requests-export-csv-btn"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700 bg-white disabled:opacity-50"
            title="Export my requests to CSV"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "CSV"}
          </button>
          <Link
            to="/requests/new"
            data-testid="my-requests-new-btn"
            className="rg-tap px-4 bg-purple-700 hover:bg-purple-800 text-white rounded-sm flex items-center gap-2 font-display uppercase tracking-wide text-sm border-2 border-purple-900"
          >
            <PackagePlus strokeWidth={3} className="h-4 w-4" />
            New Request
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          <Section
            title={`Open (${open.length})`}
            items={open}
            empty="Nothing open. Send a new request anytime."
            testid="my-requests-open"
            highlight
          />
          {completed.length > 0 && (
            <details
              className="mt-6 rg-card overflow-hidden"
              data-testid="my-requests-completed"
            >
              <summary className="cursor-pointer p-4 font-display uppercase tracking-wide text-sm text-slate-700">
                Completed ({completed.length})
              </summary>
              <div className="p-4 pt-0">
                <Section
                  title=""
                  items={completed}
                  empty=""
                  testid="my-requests-completed-list"
                />
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, items, empty, testid, highlight = false }) {
  if (items.length === 0) {
    return empty ? (
      <div className="rg-card p-8 text-center text-slate-500" data-testid={`${testid}-empty`}>
        {title && <div className="font-display text-lg uppercase mb-2">{title}</div>}
        {empty}
      </div>
    ) : null;
  }
  return (
    <div data-testid={testid}>
      {title && (
        <div className="font-display text-base uppercase tracking-wide text-slate-700 mb-3">
          {title}
        </div>
      )}
      <ul className="space-y-3">
        {items.map((s) => (
          <li key={s.id}>
            <Link
              to={`/shipments/${s.id}`}
              data-testid={`${testid}-${s.MR_Number}`}
              className={`block rg-card p-4 hover:border-slate-900 ${
                highlight ? "border-l-[6px] border-l-purple-700" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-base font-bold text-slate-900">
                    {s.MR_Number}
                  </div>
                  {s.Priority && s.Priority !== "Normal" && <PriorityBadge value={s.Priority} />}
                  {s.Estimated_Ready_Time && (
                    <EtaBadge etaText={s.Estimated_Ready_Time} etaIso={s.Estimated_Ready_Iso} />
                  )}
                </div>
                <StatusPill value={s.Status} kind="status" />
              </div>
              <div className="text-sm text-slate-700 font-bold">
                {s.Project_Station}
              </div>

              {s.Requestor_Message && (
                <div className="mt-2 bg-purple-50 border-l-4 border-purple-700 px-3 py-2 text-sm text-slate-800">
                  {s.Requestor_Message}
                </div>
              )}

              <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
                <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <StatusPill value={s.Preparation_Queue_Status} kind="prep" />
                  {s.Desired_Date && (
                    <span className="inline-flex items-center gap-1 font-bold text-amber-800">
                      <CalendarClock className="h-3 w-3" />
                      Desired {formatLocalDate(s.Desired_Date)}
                    </span>
                  )}
                  {s.Equipment_Needed && (
                    <span className="inline-flex items-center gap-1 text-slate-600">
                      <Wrench className="h-3 w-3" />
                      {s.Equipment_Needed.split("\n")[0]}
                      {s.Equipment_Needed.split("\n").length > 1 ? " …" : ""}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <ChevronRight className="h-4 w-4" />
                  {formatLocalDateTime(s.Last_Updated)}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
